// https://github.com/sersavan/shadcn-multi-select-component
import { CheckIcon, XIcon } from "lucide-react";
import * as React from "react";

import { Badge } from "../badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "../command";
import {
  FieldTrigger,
  FieldTriggerButton,
  FieldTriggerValues,
} from "../field-trigger";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";

/**
 * Props for MultiSelect component
 */
export type MultiSelectOption = {
  /** The text to display for the option. */
  label: string;
  /** The unique value associated with the option. */
  value: string;
  /** Optional icon component to display alongside the option. */
  icon?: React.ComponentType<{ className?: string }>;
};

interface MultiSelectProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * An array of option objects to be displayed in the multi-select component.
   * Each option object has a label, value, and an optional icon.
   */
  options: MultiSelectOption[];

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void;

  /** The default selected values when the component mounts. */
  defaultValue?: string[];

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;

  /**
   * If true, allows the trigger button to expand vertically to show wrapped badges.
   * When false, badges overflow into a count badge.
   * Optional, defaults to false.
   */
  multiLine?: boolean;

  /**
   * If true, disables the select all functionality.
   * Optional, defaults to false.
   */
  hideSelectAll?: boolean;

  /**
   * If true, shows search functionality in the popover.
   * If false, hides the search input completely.
   * Optional, defaults to true.
   */
  searchable?: boolean;

  /**
   * Close the popover after each selection. Defaults to false, which is standard
   * multi-select behaviour — the reader picks several values in one visit.
   *
   * Turn it on for a field with a small cap that sits ABOVE other fields in a form:
   * an open popover overlays whatever follows it, so a reader (or a test driving the
   * form top to bottom) cannot reach the next field without dismissing it first.
   */
  closeOnSelect?: boolean;

  /**
   * Text appended after the count in count-only mode (e.g. "selected" → "5 selected").
   */
  countSuffix?: string;

  "aria-label"?: string;
  "aria-describedby"?: string;
  ref?: React.RefObject<HTMLButtonElement>;
}

function RemoveBadgeIcon({ onRemove }: { onRemove: () => void }) {
  return (
    <span
      role="button"
      tabIndex={-1}
      aria-label="Remove"
      className="text-shape-neutral-ghost-foreground-muted hover:text-shape-neutral-ghost-foreground-default -mr-0.5 flex size-3.5 shrink-0 cursor-default! items-center justify-center"
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
        onRemove();
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.stopPropagation();
          event.preventDefault();
          onRemove();
        }
      }}
      onPointerDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <XIcon className="size-3" />
    </span>
  );
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  onValueChange,
  defaultValue = [],
  placeholder = "Select options",
  modalPopover = false,
  multiLine = false,
  hideSelectAll = false,
  searchable = true,
  closeOnSelect = false,
  countSuffix,
  className,
  ref,
  disabled,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedby,
  onKeyDown,
  ...props
}) => {
  const [selectedValues, setSelectedValues] =
    React.useState<string[]>(defaultValue);
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const listboxId = React.useId();
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      setIsPopoverOpen(true);
    } else if (event.key === "Backspace" && !event.currentTarget.value) {
      const newSelectedValues = [...selectedValues];
      newSelectedValues.pop();
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    }
  };

  const toggleOption = (option: string) => {
    const newSelectedValues = selectedValues.includes(option)
      ? selectedValues.filter((value) => value !== option)
      : [...selectedValues, option];
    setSelectedValues(newSelectedValues);
    onValueChange(newSelectedValues);
    if (closeOnSelect) setIsPopoverOpen(false);
  };

  const handleClear = () => {
    setSelectedValues([]);
    onValueChange([]);
    setTimeout(() => {
      triggerRef.current?.focus();
    }, 0);
  };

  const toggleAll = () => {
    if (selectedValues.length === options.length) {
      handleClear();
    } else {
      const allValues = options.map((option) => option.value);
      setSelectedValues(allValues);
      onValueChange(allValues);
    }
  };

  const getAccessibleLabel = () => {
    if (ariaLabel) {
      return ariaLabel;
    }
    if (selectedValues.length === 0) {
      return placeholder;
    }
    return `${selectedValues.length} of ${options.length} options selected`;
  };

  return (
    <>
      <Popover
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        modal={modalPopover}
      >
        <FieldTrigger
          hasValue={selectedValues.length > 0}
          disabled={disabled}
          onClear={handleClear}
        >
          <PopoverTrigger asChild>
            <FieldTriggerButton
              ref={(el: HTMLButtonElement | null) => {
                triggerRef.current = el;
                if (ref) ref.current = el as HTMLButtonElement;
              }}
              {...props}
              disabled={disabled}
              multiLine={multiLine}
              onKeyDown={(event) => {
                if (
                  !isPopoverOpen &&
                  (event.key === "ArrowDown" || event.key === "ArrowUp")
                ) {
                  event.preventDefault();
                  setIsPopoverOpen(true);
                }
                onKeyDown?.(event);
              }}
              aria-expanded={isPopoverOpen}
              aria-haspopup="listbox"
              aria-controls={listboxId}
              aria-label={getAccessibleLabel()}
              aria-describedby={ariaDescribedby}
              className={className}
            >
              {selectedValues.length > 0 ? (
                multiLine ? (
                  selectedValues.map((value) => {
                    const option = options.find((o) => o.value === value);
                    const IconComponent = option?.icon;
                    return (
                      <Badge key={value} semantic="neutral" emphasis="default">
                        {IconComponent && <IconComponent />}
                        {option?.label}
                        <RemoveBadgeIcon onRemove={() => toggleOption(value)} />
                      </Badge>
                    );
                  })
                ) : (
                  <FieldTriggerValues
                    countSuffix={countSuffix}
                    labels={selectedValues.map(
                      (value) =>
                        options.find((o) => o.value === value)?.label ?? value
                    )}
                  >
                    {selectedValues.map((value) => {
                      const option = options.find((o) => o.value === value);
                      const IconComponent = option?.icon;
                      return (
                        <Badge
                          key={value}
                          semantic="neutral"
                          emphasis="default"
                        >
                          {IconComponent && <IconComponent />}
                          {option?.label}
                          <RemoveBadgeIcon
                            onRemove={() => toggleOption(value)}
                          />
                        </Badge>
                      );
                    })}
                  </FieldTriggerValues>
                )
              ) : (
                <span className="text-shape-neutral-outlined-foreground-muted truncate text-sm">
                  {placeholder}
                </span>
              )}
            </FieldTriggerButton>
          </PopoverTrigger>
        </FieldTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
          // NOTE: I had to add this, otherwise the scrolling wouldnt work:
          //  https://github.com/shadcn-ui/ui/issues/607
          onWheel={(e) => e.stopPropagation()}
          onOpenAutoFocus={(event) => {
            const content = event.currentTarget as HTMLElement | null;
            const input =
              content?.querySelector<HTMLInputElement>("[cmdk-input]");
            if (input) {
              event.preventDefault();
              input.focus();
            }
          }}
          onKeyDown={(event) => {
            if (event.key === " ") {
              const target = event.target as HTMLInputElement;
              // Allow typing space in search input when it has content
              if (target.matches?.("[cmdk-input]") && target.value) return;

              event.preventDefault();
              const highlighted =
                event.currentTarget.querySelector<HTMLElement>(
                  "[cmdk-item][data-selected='true']"
                );
              if (highlighted) highlighted.click();
            }
          }}
        >
          <Command role="listbox" aria-multiselectable="true" id={listboxId}>
            {searchable ? (
              <CommandInput
                placeholder="Search..."
                onKeyDown={handleInputKeyDown}
              />
            ) : (
              <div className="sr-only">
                <CommandInput aria-label="Navigate options" />
              </div>
            )}
            <CommandList aria-label="Options">
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {!hideSelectAll && (
                  <CommandItem
                    key="all"
                    onSelect={toggleAll}
                    className="data-[selected=true]:bg-shape-neutral-ghost-background-hover data-[selected=true]:text-shape-neutral-ghost-foreground-default rounded-lg pr-8"
                    aria-selected={selectedValues.length === options.length}
                  >
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                      (Select All)
                    </span>
                    <span
                      className="absolute right-2 flex size-4 items-center justify-center"
                      aria-hidden="true"
                    >
                      {selectedValues.length === options.length && (
                        <CheckIcon className="text-shape-neutral-ghost-foreground-default size-4" />
                      )}
                    </span>
                  </CommandItem>
                )}
                {options.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => toggleOption(option.value)}
                      className="data-[selected=true]:bg-shape-neutral-ghost-background-hover data-[selected=true]:text-shape-neutral-ghost-foreground-default rounded-lg pr-8"
                      aria-selected={isSelected}
                    >
                      {option.icon && (
                        <option.icon className="text-shape-neutral-ghost-foreground-muted h-4 w-4" />
                      )}
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                        {option.label}
                      </span>
                      <span
                        className="absolute right-2 flex size-4 items-center justify-center"
                        aria-hidden="true"
                      >
                        {isSelected && (
                          <CheckIcon className="text-shape-neutral-ghost-foreground-default size-4" />
                        )}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              <CommandSeparator />
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
};

// MultiSelect.displayName = "MultiSelect";
